/**
 * Phase 14.13 Deliverable B — Geometry sweep 3D envelope viewer.
 *
 * Sibling to PkTrajectory3D. Sharing PkTrajectory3D would force a
 * "mode" branch through every sample / cloud / fetch path and dilute
 * the PK semantics — instead this component owns the geometry-sweep
 * visual contract:
 *   • One 3D scatter point per slice's geometry centroid
 *   • Outcome colour from per-slice mean Pk (red→amber→green ramp,
 *     RdYlGn-equivalent — see PK_RAMP_STOPS below)
 *   • Straight interceptor closing path from launch (0,0,0) to each
 *     centroid (per PM brief: when no per-sample trajectory is
 *     persisted, draw a straight line from launch to the slice's
 *     CPA proxy so the envelope visual stays complete)
 *   • Approach-vector arrows at each centroid pointing in the
 *     bearing-of-arrival direction
 *   • Origin marker at (0,0,0) for spatial anchoring
 *   • Equal data-aspect (1:1:1) via Plotly's `aspectmode: "data"`
 *
 * The 4-line legend (n_samples / hit rate / mean engagement range /
 * dominant failure mode) renders below the canvas as a small chip
 * row so the operator can read the headline at a glance.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import {
    polarToCart,
    pkColour,
    buildSceneLayout,
    PLOT_BUTTONS_TO_REMOVE,
} from "@/lib/viz3d";

// ──────────────────────────────────────────────────────────────────
// Plotly lazy loader (matches PkTrajectory3D's pattern so RTL can
// stub it cleanly).
// ──────────────────────────────────────────────────────────────────
let PlotlyImpl = null;
async function getPlotly() {
    if (PlotlyImpl) return PlotlyImpl;
    try {
        const mod = await import("plotly.js-dist-min");
        PlotlyImpl = mod.default || mod;
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[GeometrySweep3D] plotly load failed:", e);
        PlotlyImpl = null;
    }
    return PlotlyImpl;
}

// Re-export the shared helpers so existing test imports still work.
export { polarToCart, pkColour } from "@/lib/viz3d";

// Build all Plotly traces from a campaign's `result.slices` array.
// Pure function — easy to RTL.
export function buildEnvelopeTraces(slices) {
    if (!Array.isArray(slices) || slices.length === 0) return [];

    const targetXs = [];
    const targetYs = [];
    const targetZs = [];
    const targetColours = [];
    const targetTexts = [];

    const closingPaths = []; // Plotly scatter3d trace per slice (faint line)
    const arrowTraces = []; // Plotly scatter3d trace per slice (arrow segment)

    for (const s of slices) {
        const c = s.geometry_centroid || {};
        const rng = Number(c.range_m ?? 0);
        const brg = Number(c.bearing_deg ?? 0);
        const alt = Number(c.altitude_m ?? 0);
        const appr = Number(c.approach_vector_deg ?? 0);
        const pk = Number(s.pk_mean ?? 0);
        const pos = polarToCart(rng, brg, alt);
        const col = pkColour(pk);

        targetXs.push(pos.x);
        targetYs.push(pos.y);
        targetZs.push(pos.z);
        targetColours.push(col);
        const fm = s.failure_mode_mix || {};
        const dom = Object.entries(fm).sort((a, b) => b[1] - a[1])[0];
        targetTexts.push(
            `slice ${s.slice_id ?? "?"} · n=${s.n ?? 0} · Pk=${pk.toFixed(2)}` +
                (dom ? ` · ${dom[0]}` : ""),
        );

        // Faint closing path from launch.
        closingPaths.push({
            type: "scatter3d",
            mode: "lines",
            x: [0, pos.x],
            y: [0, pos.y],
            z: [0, pos.z],
            line: { color: col, width: 1.5 },
            opacity: 0.4,
            hoverinfo: "skip",
            showlegend: false,
        });
        // Approach-vector arrow — small segment of length 6% of range.
        const arrowLen = 0.06 * Math.max(rng, 1);
        const apprRad = (appr * Math.PI) / 180;
        arrowTraces.push({
            type: "scatter3d",
            mode: "lines",
            x: [pos.x, pos.x + arrowLen * Math.sin(apprRad)],
            y: [pos.y, pos.y + arrowLen * Math.cos(apprRad)],
            z: [pos.z, pos.z],
            line: { color: col, width: 3 },
            opacity: 0.9,
            hoverinfo: "skip",
            showlegend: false,
        });
    }

    const targetTrace = {
        type: "scatter3d",
        mode: "markers",
        x: targetXs,
        y: targetYs,
        z: targetZs,
        marker: { size: 5, color: targetColours, line: { color: "#0f172a", width: 0.5 } },
        text: targetTexts,
        hovertemplate: "%{text}<br>(E %{x:.0f} m, N %{y:.0f} m, Up %{z:.0f} m)<extra></extra>",
        name: "Target start (per slice)",
    };

    const launchTrace = {
        type: "scatter3d",
        mode: "markers",
        x: [0],
        y: [0],
        z: [0],
        marker: {
            size: 8,
            color: "#fde68a",
            symbol: "diamond",
            line: { color: "#facc15", width: 1.0 },
        },
        name: "Launch (0,0,0)",
        hovertemplate: "Launch (0,0,0)<extra></extra>",
    };

    return [launchTrace, targetTrace, ...closingPaths, ...arrowTraces];
}

export function buildEnvelopeLegend(slices) {
    if (!Array.isArray(slices) || slices.length === 0) {
        return {
            n_samples: 0,
            hit_rate_pct: 0,
            mean_range_m: 0,
            dominant_failure_mode: "n/a",
        };
    }
    let n = 0;
    let intercepts = 0;
    let weightedRange = 0;
    const fmCounts = {};
    for (const s of slices) {
        const ns = Number(s.n ?? 0);
        n += ns;
        const fm = s.failure_mode_mix || {};
        for (const [k, v] of Object.entries(fm)) {
            fmCounts[k] = (fmCounts[k] || 0) + Number(v || 0);
            if (k === "intercept") intercepts += Number(v || 0);
        }
        const rng = Number((s.geometry_centroid || {}).range_m ?? 0);
        weightedRange += rng * ns;
    }
    const dom = Object.entries(fmCounts).sort((a, b) => b[1] - a[1])[0];
    return {
        n_samples: n,
        hit_rate_pct: n ? (100 * intercepts) / n : 0,
        mean_range_m: n ? weightedRange / n : 0,
        dominant_failure_mode: dom ? dom[0] : "n/a",
    };
}

export default function GeometrySweep3D({
    campaign,
    height = 480,
    "data-testid": dataTestId = "geometry-sweep-3d",
}) {
    const containerRef = useRef(null);
    const [plotlyReady, setPlotlyReady] = useState(false);
    const [plotlyAvailable, setPlotlyAvailable] = useState(true);
    const slices = campaign?.result?.slices || [];
    const traces = useMemo(() => buildEnvelopeTraces(slices), [slices]);
    const legend = useMemo(() => buildEnvelopeLegend(slices), [slices]);

    useEffect(() => {
        let cancelled = false;
        getPlotly().then((mod) => {
            if (cancelled) return;
            if (mod) {
                setPlotlyReady(true);
            } else {
                setPlotlyAvailable(false);
            }
        });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!plotlyReady) return;
        if (!containerRef.current) return;
        if (!traces.length) return;
        const layout = buildSceneLayout();
        // Re-import getPlotly synchronously (cached).
        getPlotly().then((mod) => {
            if (!mod) return;
            mod.react(containerRef.current, traces, layout, {
                responsive: true,
                displaylogo: false,
                modeBarButtonsToRemove: PLOT_BUTTONS_TO_REMOVE,
            });
        });
    }, [plotlyReady, traces]);

    if (!slices.length) {
        return (
            <div
                className="rounded-md border border-dashed border-slate-700 bg-slate-900/40 p-6 text-sm text-muted-foreground text-center"
                data-testid={`${dataTestId}-empty`}
            >
                No slices to render — the campaign may not have completed yet.
            </div>
        );
    }
    if (!plotlyAvailable) {
        return (
            <div
                className="rounded-md border border-dashed border-amber-700/50 bg-amber-900/10 p-6 text-sm text-amber-200 text-center"
                data-testid={`${dataTestId}-unavailable`}
            >
                3D viewer unavailable in this build.
            </div>
        );
    }
    return (
        <div data-testid={dataTestId} className="space-y-3">
            <div className="rounded-md border border-slate-800 overflow-hidden">
                {plotlyReady ? (
                    <div ref={containerRef} style={{ width: "100%", height }} />
                ) : (
                    <Skeleton className="w-full" style={{ height }} />
                )}
            </div>
            <div className="flex flex-wrap gap-3 text-xs">
                <span
                    data-testid={`${dataTestId}-legend-n-samples`}
                    className="rounded bg-slate-800/60 px-2 py-1"
                >
                    n_samples: <strong>{legend.n_samples}</strong>
                </span>
                <span
                    data-testid={`${dataTestId}-legend-hit-rate`}
                    className="rounded bg-slate-800/60 px-2 py-1"
                >
                    hit rate: <strong>{legend.hit_rate_pct.toFixed(1)}%</strong>
                </span>
                <span
                    data-testid={`${dataTestId}-legend-mean-range`}
                    className="rounded bg-slate-800/60 px-2 py-1"
                >
                    mean engagement range:{" "}
                    <strong>{legend.mean_range_m.toFixed(0)} m</strong>
                </span>
                <span
                    data-testid={`${dataTestId}-legend-dominant`}
                    className="rounded bg-slate-800/60 px-2 py-1"
                >
                    dominant: <strong>{legend.dominant_failure_mode}</strong>
                </span>
            </div>
        </div>
    );
}
